import { Button, Flex, Tag, TagLeftIcon, Text } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import {
  RiArrowRightLine,
  RiCheckLine,
  RiDraftLine,
  RiEditLine,
} from "react-icons/ri";
import { NavLink, useParams } from "react-router-dom";
import { DeleteFormTemplateButton } from "src/components/Buttons/DeleteFormTemplateButton";
import { SwitchInput } from "src/components/Inputs/SwitchInput";
import { AdminCard } from "src/components/Layout/AdminCard";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { Glossary } from "src/components/Text/Glossary";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { evictFormTemplateCache } from "src/services/apolloCacheManager";
import {
  FORM_TEMPLATE_TAB_HASH,
  FormTemplates,
} from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { FormTemplateSummary } from "./components/FormTemplateSummary";
import { UPDATE_FORM_TEMPLATE_ACTIVE_STATUS } from "./graphql/mutations";
import { useFormBuilderV1Feature } from "./hooks/useFormBuilderV1Feature";
import { FormTemplateMetadata } from "./types/common";

interface Props {
  formTemplate: FormTemplateMetadata;
}

export const FormTemplateCard: React.FC<Props> = ({ formTemplate }) => {
  const organization = useOrganization();
  const { isFormBuilderReadOnly, isFormBuilderFullEdit } =
    useFormBuilderV1Feature();

  const permissions = useUserPermissions();

  const { enrollmentPeriodId = "" } = useParams();
  const toast = useAvelaToast();
  const [updateFormTemplateActiveStatus] = useRemoteDataMutation<
    GQL.UpdateFormTemplateActiveStatus,
    GQL.UpdateFormTemplateActiveStatusVariables
  >(UPDATE_FORM_TEMPLATE_ACTIVE_STATUS, {
    update: evictFormTemplateCache,
  });

  const { confirm: confirmActiveChange, confirmationDialog: activateDialog } =
    useConfirmationDialog({
      header: "Activate form?",
      body: <FormTemplateSummary formTemplate={formTemplate} />,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Activate form",
      },
      translate: true,
    });

  const {
    confirm: confirmInactiveChange,
    confirmationDialog: inactivateDialog,
  } = useConfirmationDialog({
    header: "Deactivate form?",
    body: (
      <Text>
        <Glossary>
          This will prevent students from starting a new form responses and move
          any existing ones they may have to their History page. It has no
          effect on the admin panel
        </Glossary>
      </Text>
    ),
    cancelButton: {
      label: "Cancel",
    },
    confirmButton: {
      label: "Deactivate form",
    },
    translate: true,
  });

  const handleSubmit = useCallback(async () => {
    try {
      await updateFormTemplateActiveStatus({
        variables: {
          id: formTemplate.id,
          form_template_status: !formTemplate.active,
        },
      });
      toast({
        title: "Success",
        description: `Form ${
          formTemplate.active ? "deactivated" : "activated"
        }`,
        status: "success",
        duration: 2000,
      });
    } catch (e) {
      toast({
        title: "Something went wrong",
        description: "Failed to change form status",
        status: "error",
        duration: 2000,
      });
    }
  }, [formTemplate, toast, updateFormTemplateActiveStatus]);

  const handleActiveChangeClick = useCallback(async () => {
    if (formTemplate.active) {
      if (!(await confirmInactiveChange())) {
        return;
      }
    } else {
      if (!(await confirmActiveChange())) {
        return;
      }
    }

    handleSubmit();
  }, [
    confirmActiveChange,
    handleSubmit,
    confirmInactiveChange,
    formTemplate.active,
  ]);

  const editRouteBasedOnPermission = useMemo(
    () =>
      organization
        .map((org) =>
          FormTemplates.edit(
            org,
            enrollmentPeriodId,
            formTemplate.id,
            isFormBuilderReadOnly || isFormBuilderFullEdit
              ? FORM_TEMPLATE_TAB_HASH.CONTENT
              : FORM_TEMPLATE_TAB_HASH.STATUSES
          )
        )
        .withDefault("#"),
    [
      isFormBuilderReadOnly,
      isFormBuilderFullEdit,
      organization,
      enrollmentPeriodId,
      formTemplate.id,
    ]
  );

  const rulesNextFormTemplates = useMemo(
    () =>
      formTemplate.form_template_rule?.rules.map((rule) => [
        rule.status_rule?.next_form_template.name,
      ]),
    [formTemplate]
  );

  const flags = useFlags(["form-builder-draft-mode"]);

  return (
    <>
      <AdminCard
        tag={
          flags["form-builder-draft-mode"].enabled ? (
            <>
              <ModeTag mode={formTemplate.mode} />
              <Tag fontSize="sm">
                Form version: {formTemplate.schema_version}
              </Tag>
            </>
          ) : null
        }
        title={formTemplate.name}
        description={formTemplate.description}
        switches={
          !permissions.isLoading &&
          permissions.hasOne("form_template:update") ? (
            <SwitchInput<boolean>
              checked={[true, "Active"]}
              unchecked={[false, "Inactive"]}
              value={formTemplate.active}
              onChange={handleActiveChangeClick}
              switchDirection="row"
              isDisabled={
                formTemplate.mode === GQL.form_template_mode_enum.Draft &&
                !formTemplate.active
              }
            />
          ) : (
            <Text fontSize="sm" fontWeight="400">
              {formTemplate.active ? "Active" : "Inactive"}
            </Text>
          )
        }
        actions={
          <>
            {!permissions.isLoading &&
            permissions.hasOne("form_template:delete") ? (
              <DeleteFormTemplateButton
                id={formTemplate.id}
                formTemplateName={formTemplate.name}
                buttonProps={{ colorScheme: "gray", size: "sm" }}
              />
            ) : null}
            {!permissions.isLoading &&
            permissions.hasAll([
              "form_template:read",
              "form_template:update",
            ]) ? (
              <Button
                as={NavLink}
                aria-label="Edit action click"
                colorScheme="gray"
                variant="outline"
                leftIcon={<RiEditLine />}
                size="sm"
                to={editRouteBasedOnPermission}
              >
                Edit
              </Button>
            ) : null}

            {formTemplate.schema_version === "2.0.0" &&
            permissions.hasOne("form_template:read") ? (
              <Button
                as={NavLink}
                aria-label="Edit action click"
                colorScheme="gray"
                variant="outline"
                leftIcon={<RiArrowRightLine />}
                size="sm"
                to={organization
                  .map((org) =>
                    FormTemplates.view(
                      org,
                      enrollmentPeriodId,
                      formTemplate.id,
                      FORM_TEMPLATE_TAB_HASH.CONTENT
                    )
                  )
                  .withDefault("#")}
              >
                View
              </Button>
            ) : null}
          </>
        }
      >
        <Flex direction="column" gap="2">
          {rulesNextFormTemplates?.length && (
            <Flex direction="column">
              <Text fontSize="sm" color="gray.700">
                Flows into
              </Text>

              {rulesNextFormTemplates?.map((nextFormTemplate, index) => (
                <Text key={index} color="blackAlpha.600">
                  {nextFormTemplate}
                </Text>
              ))}
            </Flex>
          )}
        </Flex>
      </AdminCard>
      {activateDialog}
      {inactivateDialog}
    </>
  );
};

type ModeCardProps = { mode: GQL.form_template_mode_enum };
function ModeTag(props: ModeCardProps) {
  let icon;
  let color;
  switch (props.mode) {
    case GQL.form_template_mode_enum.Draft: {
      icon = <TagLeftIcon as={RiDraftLine} />;
      color = "gray";
      break;
    }
    case GQL.form_template_mode_enum.Published: {
      icon = <TagLeftIcon as={RiCheckLine} />;
      color = "green";
      break;
    }
  }

  return (
    <Tag colorScheme={color}>
      {icon}
      {props.mode}
    </Tag>
  );
}
